import { useEffect, useMemo, useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import Layout from "../components/Layout";
import api from "../lib/api";
import { Button } from "../components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { ArrowLeft, Minus, TrendingUp, TrendingDown, Equal, Sparkles, AlertCircle } from "lucide-react";
import { toast } from "sonner";

export default function Compare() {
  const [programs, setPrograms] = useState([]);
  const [params, setParams] = useSearchParams();
  const a = params.get("a") || "";
  const b = params.get("b") || "";
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    api.get("/programs").then((r) => setPrograms(r.data || []));
  }, []);

  useEffect(() => {
    if (!a || !b || a === b) {
      setData(null);
      return;
    }
    setLoading(true);
    api
      .get("/compare", { params: { a, b } })
      .then((r) => setData(r.data))
      .catch((err) => toast.error(err.response?.data?.detail || "Could not compare"))
      .finally(() => setLoading(false));
  }, [a, b]);

  const setAB = (key, val) => {
    const next = new URLSearchParams(params);
    if (val) next.set(key, val);
    else next.delete(key);
    setParams(next, { replace: true });
  };

  return (
    <Layout>
      <div className="mb-6">
        <Link to="/" className="inline-flex items-center text-xs text-slate-500 hover:text-slate-900" data-testid="compare-back">
          <ArrowLeft className="w-3 h-3 mr-1" /> All programs
        </Link>
        <p className="label-overline mt-3">Compare</p>
        <h1 className="font-heading text-4xl sm:text-5xl tracking-tight font-semibold mt-1">Side-by-side</h1>
        <p className="text-slate-600 mt-2 max-w-2xl">
          Pick two programs to line up every indicator. Shared indicators show deltas and % to target; unique
          indicators are flagged so you can see what each program covers that the other doesn't.
        </p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-8">
        <ProgramPicker
          label="Program A"
          value={a}
          options={programs.filter((p) => p.id !== b)}
          onChange={(v) => setAB("a", v)}
          testid="compare-picker-a"
        />
        <ProgramPicker
          label="Program B"
          value={b}
          options={programs.filter((p) => p.id !== a)}
          onChange={(v) => setAB("b", v)}
          testid="compare-picker-b"
        />
      </div>

      {!a || !b ? (
        <EmptyState />
      ) : loading ? (
        <div className="text-slate-500 text-sm">Loading…</div>
      ) : data ? (
        <CompareView data={data} />
      ) : null}
    </Layout>
  );
}

function ProgramPicker({ label, value, options, onChange, testid }) {
  return (
    <div className="bg-white border border-slate-200 rounded-sm p-4">
      <p className="label-overline">{label}</p>
      <Select value={value} onValueChange={onChange}>
        <SelectTrigger className="mt-2" data-testid={testid}>
          <SelectValue placeholder="Choose a program…" />
        </SelectTrigger>
        <SelectContent>
          {options.length === 0 && (
            <div className="px-3 py-2 text-xs text-slate-500">No other programs available.</div>
          )}
          {options.map((p) => (
            <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>
          ))}
        </SelectContent>
      </Select>
    </div>
  );
}

function EmptyState() {
  return (
    <div className="border border-dashed border-slate-300 bg-white p-12 text-center" data-testid="compare-empty">
      <Sparkles className="w-6 h-6 text-slate-400 mx-auto" />
      <p className="label-overline mt-3">Pick two programs</p>
      <h3 className="font-heading text-2xl mt-1">Ready when you are</h3>
      <p className="text-slate-600 mt-2 max-w-md mx-auto text-sm">
        Select a program on each side to see indicator totals, % to target, and dirty-data debt across them.
      </p>
    </div>
  );
}

function CompareView({ data }) {
  const { a: snapA, b: snapB, indicators, shared_indicators, unique_to_a, unique_to_b } = data;

  const stats = useMemo(() => ([
    { label: "Indicators", a: snapA.indicator_count, b: snapB.indicator_count },
    { label: "Clean rows", a: snapA.rows.clean, b: snapB.rows.clean },
    { label: "Dirty rows", a: snapA.rows.dirty, b: snapB.rows.dirty, warnHigher: true },
    { label: "Superseded rows", a: snapA.rows.superseded, b: snapB.rows.superseded },
  ]), [snapA, snapB]);

  return (
    <div className="space-y-8" data-testid="compare-view">
      {/* Program headers */}
      <div className="grid grid-cols-2 gap-4">
        <ProgramHeaderCard snap={snapA} side="a" />
        <ProgramHeaderCard snap={snapB} side="b" />
      </div>

      {/* Overlap summary */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
        <SummaryPill label="Shared indicators" value={shared_indicators} tone="emerald" testid="compare-shared"/>
        <SummaryPill label={`Only in ${snapA.program.name}`} value={unique_to_a} tone="sky" testid="compare-unique-a"/>
        <SummaryPill label={`Only in ${snapB.program.name}`} value={unique_to_b} tone="sky" testid="compare-unique-b"/>
      </div>

      {/* Rollup stats */}
      <div>
        <h2 className="font-heading text-2xl font-semibold mb-3">Data rollup</h2>
        <div className="bg-white border border-slate-200 rounded-sm overflow-hidden">
          <table className="w-full text-sm" data-testid="compare-rollup-table">
            <thead>
              <tr className="bg-slate-900 text-white text-left">
                <th className="px-4 py-3 font-medium"></th>
                <th className="px-4 py-3 font-medium text-right">{snapA.program.name}</th>
                <th className="px-4 py-3 font-medium text-right">{snapB.program.name}</th>
                <th className="px-4 py-3 font-medium text-right w-24">Δ</th>
              </tr>
            </thead>
            <tbody>
              {stats.map((s) => {
                const d = (s.a || 0) - (s.b || 0);
                return (
                  <tr key={s.label} className="border-t border-slate-100">
                    <td className="px-4 py-3 text-slate-600">{s.label}</td>
                    <td className="px-4 py-3 text-right tabular font-medium">{s.a ?? 0}</td>
                    <td className="px-4 py-3 text-right tabular font-medium">{s.b ?? 0}</td>
                    <td className={`px-4 py-3 text-right tabular text-xs ${d === 0 ? "text-slate-400" : s.warnHigher ? (d > 0 ? "text-red-700" : "text-emerald-700") : (d > 0 ? "text-emerald-700" : "text-red-700")}`}>
                      {d === 0 ? "—" : (d > 0 ? `+${d}` : d)}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      {/* Per-indicator comparison */}
      <div>
        <h2 className="font-heading text-2xl font-semibold mb-3">Indicators</h2>
        <div className="bg-white border border-slate-200 rounded-sm overflow-hidden">
          <table className="w-full text-sm" data-testid="compare-indicator-table">
            <thead>
              <tr className="bg-slate-900 text-white text-left">
                <th className="px-4 py-3 font-medium">Indicator</th>
                <th className="px-4 py-3 font-medium text-right w-40">{snapA.program.name}</th>
                <th className="px-4 py-3 font-medium text-right w-40">{snapB.program.name}</th>
                <th className="px-4 py-3 font-medium text-right w-24">Δ</th>
              </tr>
            </thead>
            <tbody>
              {indicators.length === 0 && (
                <tr>
                  <td colSpan={4} className="px-4 py-8 text-center text-slate-500">
                    Neither program has any indicators yet.
                  </td>
                </tr>
              )}
              {indicators.map((it) => (
                <IndicatorRow key={it.code} item={it} />
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

function ProgramHeaderCard({ snap, side }) {
  const p = snap.program;
  return (
    <div className="bg-white border border-slate-200 rounded-sm p-5" data-testid={`compare-header-${side}`}>
      <p className="label-overline">Program {side.toUpperCase()}</p>
      <h3 className="font-heading text-xl font-semibold mt-1">{p.name}</h3>
      {p.description && <p className="text-sm text-slate-600 mt-1 line-clamp-2">{p.description}</p>}
      <p className="text-xs text-slate-500 mt-3">
        <span className="font-medium text-slate-700">{snap.indicator_count}</span> indicators ·
        {" "}<span className="font-medium text-slate-700">{snap.rows.clean}</span> clean rows
        {snap.rows.dirty > 0 && <>
          {" "}· <span className="text-amber-700 inline-flex items-center gap-1"><AlertCircle className="w-3 h-3"/>{snap.rows.dirty} dirty</span>
        </>}
        {snap.latest_upload_at && <>{" "}· last upload {new Date(snap.latest_upload_at).toLocaleDateString()}</>}
      </p>
      <Link
        to={`/programs/${p.id}`}
        className="inline-block mt-3 text-xs text-slate-900 underline hover:text-slate-600"
      >
        Open program →
      </Link>
    </div>
  );
}

function SummaryPill({ label, value, tone, testid }) {
  const toneClass = tone === "emerald"
    ? "bg-emerald-50 border-emerald-200 text-emerald-900"
    : "bg-sky-50 border-sky-200 text-sky-900";
  return (
    <div className={`rounded-sm border ${toneClass} px-4 py-3`} data-testid={testid}>
      <p className="text-[11px] uppercase tracking-wider opacity-80">{label}</p>
      <p className="font-mono text-2xl font-semibold mt-1">{value}</p>
    </div>
  );
}

function IndicatorRow({ item }) {
  const a = item.a, b = item.b;
  const both = a && b && a.data_type === "numeric" && b.data_type === "numeric";
  const delta = both ? (Number(a.total || 0) - Number(b.total || 0)) : null;
  const deltaIcon = delta === null ? <Minus className="w-3 h-3 text-slate-400"/>
    : delta > 0 ? <TrendingUp className="w-3 h-3 text-emerald-700"/>
    : delta < 0 ? <TrendingDown className="w-3 h-3 text-red-700"/>
    : <Equal className="w-3 h-3 text-slate-400"/>;

  return (
    <tr className="border-t border-slate-100 hover:bg-slate-50" data-testid={`compare-row-${item.code}`}>
      <td className="px-4 py-3">
        <div className="font-mono text-xs text-slate-500">{item.code}</div>
        <div className="text-slate-800">{item.name || "—"}</div>
        <div className="text-[11px] text-slate-400">
          {(a?.data_type || b?.data_type || "—")}{(a?.unit || b?.unit) ? ` · ${a?.unit || b?.unit}` : ""}
        </div>
      </td>
      <SideCell side={a} />
      <SideCell side={b} />
      <td className="px-4 py-3 text-right text-xs tabular">
        {delta === null ? (
          <span className="inline-flex items-center gap-1 text-slate-400">{deltaIcon} —</span>
        ) : (
          <span className={`inline-flex items-center gap-1 ${delta > 0 ? "text-emerald-700" : delta < 0 ? "text-red-700" : "text-slate-400"}`}>
            {deltaIcon} {delta === 0 ? "0" : (delta > 0 ? `+${delta}` : delta)}
          </span>
        )}
      </td>
    </tr>
  );
}

function SideCell({ side }) {
  if (!side) {
    return (
      <td className="px-4 py-3 text-right text-xs text-slate-400 tabular">—</td>
    );
  }
  const total = side.data_type === "numeric" ? (side.total ?? 0) : (side.total ? "✓" : "—");
  const pct = side.percent;
  return (
    <td className="px-4 py-3 text-right tabular">
      <div className="font-medium text-slate-800">{total}</div>
      {pct != null && (
        <div
          className={`text-[11px] ${pct >= 100 ? "text-emerald-700" : pct >= 50 ? "text-amber-700" : "text-slate-500"}`}
          title={`Target ${side.target_value}${side.target_period ? ` by ${side.target_period}` : ""}`}
        >
          {pct}% of target
        </div>
      )}
    </td>
  );
}
